import { User } from 'services/interface';

export const USER_ROLES_ACCESS_DENIED = {
    admin: [],
    /** Not in an organization + API org_role null (MDCL-16412). Commercial shell users are paid; SPS matches this role, not license flags. */
    'non-org-user': [],
    user: ['notifications', 'workflows', 'engines', 'secure-pipeline-studio'],
    analyst: ['analyze', 'security', 'notifications', 'workflows', 'engines', 'secure-pipeline-studio', 'account/api-key'],
} as const;

export type UserRole = keyof typeof USER_ROLES_ACCESS_DENIED;

const normalizeEmail = (email: string | undefined): string => {
    return (email ?? '').trim().toLowerCase();
};

const getMdProxyAdminEmails = (): string[] => {
    return (process.env.GATSBY_MDPROXY_ADMIN ?? '')
        .split(',')
        .map((email) => normalizeEmail(email))
        .filter((email) => email.length > 0);
};

const canAccessCswg = (user: User | null | undefined): boolean => {
    const userEmail = normalizeEmail(user?.email);
    const allowedAdmins = getMdProxyAdminEmails();

    return allowedAdmins.includes(userEmail);
};

export const isInOrganization = (user: User | null | undefined): boolean => {
    return Boolean(user?.organization?.organization_id);
};

export const isPaidUser = (user: User | null | undefined): boolean => {
    return user?.licenseInfo?.paidUser === 1 || user?.paid_user === 1;
};

const getUserRole = (user: User | null | undefined): UserRole => {
    if (user == null) {
        return 'user';
    }

    if (!isInOrganization(user) && user.org_role === null) {
        return 'non-org-user';
    }

    if (!user.org_role) {
        return 'user';
    }

    const role = user.org_role;

    if (!(role in USER_ROLES_ACCESS_DENIED)) {
        return 'user';
    }

    return role;
};

const canAccessSecurePipelineStudio = (user: User | null | undefined): boolean => {
    if (isInOrganization(user)) {
        return user?.org_role === 'admin' || user?.org_role === 'user' || user?.org_role === 'analyst';
    }
    return getUserRole(user) === 'non-org-user';
};

export const canEditSecurePipelineStudio = (user: User | null | undefined): boolean => {
    if (isInOrganization(user)) {
        return user?.org_role === 'admin';
    }
    return getUserRole(user) === 'non-org-user';
};

export const canAccessHomePage = (user: User | null | undefined): boolean => {
    return !(isInOrganization(user) && user?.org_role === 'analyst');
};

export const canAccessReports = (user: User | null | undefined): boolean => {
    if (user?.isLoggedIn !== true) {
        return false;
    }
    return isPaidUser(user) || isInOrganization(user);
};

export const shouldRedirectToDashboard = (user: User | null | undefined): boolean => {
    if (user?.isLoggedIn !== true) {
        return false;
    }
    if (isPaidUser(user)) {
        return true;
    }
    return (
        isInOrganization(user) &&
        (user?.org_role === 'user' || user?.org_role === 'admin' || user?.org_role === 'analyst')
    );
};

/** Pages that require an authenticated session, regardless of role. */
const LOGIN_REQUIRED_PAGES: readonly string[] = [
    'dashboard',
    'reports',
    'analyze',
    'account/api-key',
    'secure-pipeline-studio',
    'workflows',
    'engines',
    'profile',
    'cve-vulnerabilities',
    'settings',
];

export const canAccessPage = (user: User | null | undefined, page: string): boolean => {
    if (LOGIN_REQUIRED_PAGES.includes(page) && user?.isLoggedIn !== true) {
        return false;
    }

    if (page === 'account/api-key') {
        return !(USER_ROLES_ACCESS_DENIED[getUserRole(user)] as readonly string[]).includes('account/api-key');
    }

    if (page === 'store') {
        return Boolean(user?.isOnlineStore);
    }

    if (page === 'cswg') {
        return canAccessCswg(user);
    }

    if (page === 'workflows') {
        return canAccessSecurePipelineStudio(user);
    }

    if (page === 'engines') {
        return canAccessSecurePipelineStudio(user);
    }

    if (page === 'secure-pipeline-studio') {
        return canAccessSecurePipelineStudio(user);
    }

    if (page === 'notifications' && !isInOrganization(user)) {
        return false;
    }

    if (page === 'security' && !isInOrganization(user) && !isPaidUser(user)) {
        return false;
    }

    const role = getUserRole(user);
    const deniedPages = USER_ROLES_ACCESS_DENIED[role] as readonly string[];

    return !deniedPages.includes(page);
};

export const canAccessPath = (user: User | null | undefined, pathname: string): boolean => {
    if (!pathname) return true;

    const pathParts = pathname.split('/').filter(Boolean);

    if (pathParts.length === 0) return true; // Home page

    const [section, tab] = pathParts;

    // Dashboard tabs: check 'dashboard' access only (not individual tabs)
    if (section === 'dashboard') {
        return canAccessPage(user, 'dashboard');
    }

    // Settings tabs: check specific tab access
    if (section === 'settings' && tab) {
        return canAccessPage(user, tab);
    }

    // Secure Pipeline Studio: check workflows and engines access
    if (section === 'secure-pipeline-studio') {
        // Check if user has access to secure-pipeline-studio
        if (!canAccessPage(user, 'secure-pipeline-studio')) {
            return false;
        }
        // If there's a sub-path (workflows or engines), check that too
        if (tab) {
            return canAccessPage(user, tab);
        }
        return canAccessPage(user, 'secure-pipeline-studio');
    }

    // CSWG: always enforce access for the entire section.
    if (section === 'cswg') {
        return canAccessPage(user, 'cswg');
    }

    // Dedicated documentation route — requires an authenticated session.
    if (section === 'account' && tab === 'api-key') {
        return canAccessPage(user, 'account/api-key');
    }

    // Top-level pages: check page name
    if (pathParts.length === 1) {
        return canAccessPage(user, section);
    }

    return true;
};
